import { StrictOmit } from "@instacover/utils"
import { UpdateSpec } from "dexie"

import { Detail, DetailSlim, stripDetailBinaryData, Tables } from "@/database/helpers/tables"
import { Repository } from "@/database/helpers/types"
import { PartialBy } from "@/utils/types"

import { deletedFactory } from "../helpers/filters"

type UpsertData = PartialBy<Parameters<Tables["detail"]["add"]>[0], "createdAt" | "updatedAt">
type UpdateData = StrictOmit<UpdateSpec<Detail>, "dexieId">

type FindAllParams = {
  includeDeleted?: boolean
  sessionId: string
}

type FindByMainPhotoParams = {
  includeDeleted?: boolean
  instahomePhotoId: string
}

export class DetailRepository extends Repository {
  async add(data: UpsertData) {
    const currentDate = new Date()
    await this.dexie.detail.add({
      ...data,
      createdAt: data.createdAt ?? currentDate,
      updatedAt: data.updatedAt ?? currentDate,
    })
  }

  async upsert(data: UpsertData) {
    await this.dexie.transaction("rw", this.dexie.detail, async () => {
      const record = await this.dexie.detail.where("id").equals(data.id).first()
      if (record) {
        await this.dexie.detail.update(record.dexieId, { ...data })
        return
      }

      await this.add(data)
    })
  }

  async findAll(params: FindAllParams): Promise<DetailSlim[]> {
    const { sessionId, includeDeleted = false } = params

    const details = await this.dexie.detail
      .where("instahomeSessionId")
      .anyOf(sessionId)
      .and(deletedFactory(includeDeleted))
      .toArray()

    return details.map(stripDetailBinaryData)
  }

  async findByMainPhoto(params: FindByMainPhotoParams): Promise<DetailSlim[]> {
    const { instahomePhotoId, includeDeleted = false } = params

    const details = await this.dexie.detail
      .where("instahomePhotoId")
      .equals(instahomePhotoId)
      .and(deletedFactory(includeDeleted))
      .toArray()

    return details.map(stripDetailBinaryData)
  }

  async findById(id: string): Promise<DetailSlim | undefined> {
    const detail = await this.dexie.detail.where("id").equals(id).first()
    return detail ? stripDetailBinaryData(detail) : undefined
  }

  /** Returns full detail with binary data — for blob loading and upload */
  async findByIdFull(id: string): Promise<Detail | undefined> {
    return await this.dexie.detail.where("id").equals(id).first()
  }

  async markAsDeleted(id: string) {
    await this.dexie.transaction("rw", this.dexie.detail, async () => {
      const detail = await this.findById(id)
      if (!detail) {
        return
      }

      await this.dexie.detail.update(detail.dexieId, { deleted: true })
    })
  }

  async update(id: string, data: UpdateData) {
    await this.dexie.transaction("rw", this.dexie.detail, async () => {
      const detail = await this.findById(id)
      if (!detail) {
        return
      }

      await this.dexie.detail.update(detail.dexieId, { ...data, updatedAt: new Date() })
    })
  }
}
